<script>
import Draggable from 'vuedraggable';
import cloneDeep from 'lodash/cloneDeep';
import { generateId } from '@shared/services/Utils';
import BasePositionSelector from '@shared/components/BasePositionSelector.vue';

const CTA_TYPES = {
  BUTTON: {
    type: 'BUTTON',
    isPaused: false,
    position: 'right-bottom',
    startAt: '',
    endAt: '',
    text: '',
    url: '',
  },
  INFO: {
    type: 'INFO',
    isPaused: false,
    isClosed: false,
    position: 'right-bottom',
    startAt: '',
    endAt: '',
    title: '',
    text: '',
  },
  CARD: {
    type: 'CARD',
    isPaused: false,
    position: 'right-bottom',
    startAt: '',
    endAt: '',
    title: '',
    text: '',
    ctaText: '',
    ctaUrl: '',
  },
  STOP: {
    type: 'STOP',
    isPaused: true,
    position: 'right-bottom',
    startAt: '',
    endAt: '',
    title: '',
    text: '',
    ctaText: '',
    ctaUrl: '',
  },
};

export default {
  components: { Draggable, BasePositionSelector },
  props: {
    value: {
      type: Array,
      default: () => ([]),
    },
    v: {
      type: Object,
      required: true,
    },
    newCta: {
      type: Object,
      default: () => (null),
    },
  },
  data() {
    const ctas = [...this.value].map((v) => {
      if (v.id) {
        return v;
      }

      return {
        ...v,
        id: generateId(),
      };
    });

    return {
      hasTipsOpen: false,
      newCtaType: 'BUTTON',
      isEditingCta: false,
      ctas,
    };
  },
  computed: {
    dragHandleTitle() {
      return this.$t('ctas.drag_handle_title');
    },
    addCtaButtonText() {
      return this.$t('ctas.add_cta');
    },
  },
  watch: {
    value(value) {
      this.ctas = [...value];
    },
  },
  methods: {
    addCta() {
      this.$emit('update:new-cta', {
        id: generateId(),
        ...CTA_TYPES[this.newCtaType],
      });
    },
    saveCta() {
      this.v.newCta.$touch();

      if (this.v.newCta.$error || this.v.newCta.$pending) {
        return;
      }

      if (this.isEditingCta) {
        this.ctas = this.ctas.map((v) => {
          if (v.id === this.newCta.id) {
            return { ...this.newCta };
          }

          return v;
        });
      } else {
        this.ctas = [...this.ctas, this.newCta];
      }

      this.$emit('input', this.ctas);
      this.closeNewCta();
    },
    editCta(cta) {
      this.isEditingCta = true;
      this.$emit('update:new-cta', cloneDeep(cta));
    },
    closeNewCta() {
      this.isEditingCta = false;
      this.v.newCta.$reset();
      this.$emit('update:new-cta', null);
    },
    removeCta(cta) {
      this.ctas = this.ctas.filter((v) => v !== cta);
      this.$emit('input', this.ctas);
    },
  },
  destroyed() {
    this.closeNewCta();
  },
  i18n: {
    messages: {
      en: {
        ctas: {
          title: 'Add chapters and timestamps to your video',
          text: 'Timestamping allows you to create links to specific moments in your video. The expected format is "<strong>00:00</strong>", that is: <strong>minutes, colon, seconds</strong>. Example: if you have a chapter that starts at 3m30s, you should put 03:30. And if it starts at 2h13m30s, you should indicate 133:30.',
          type_label: 'Choose a type',
          type_button: 'Link',
          type_info: 'Tooltip',
          type_card: 'Card',
          type_stop: 'Pause / Stop',
          chapter_label: 'Chapter label',
          time_placeholder: '00:00',
          drag_handle_title: 'Click and drag to reorder',
          add_cta: 'Add a chapter',
          labels: {
            text: 'Text',
            isClosed: 'Minimized by default',
            startAt: 'Start at',
            endAt: 'End at',
            title: 'Title',
            ctaText: 'Link Text',
            ctaUrl: 'URL',
            position: 'Position',
          },
        },
      },
      fr: {
        ctas: {
          title: 'Ajouter des interactions',
          text: `
            <p>
              🎯 <strong>Rendez votre vidéo plus interactive et engageante</strong>
            </p>
            <p>
            Vous pouvez enrichir votre vidéo en ajoutant des <strong>boutons</strong>,
            des <strong>bulles d’information</strong>, des <strong>bulles cliquables</strong>
            ou encore des <strong>arrêts sur image</strong> à des moments clés.
            Ces éléments permettent de rediriger vers des ressources externes
            ou d’apporter des compléments utiles au bon moment.
            </p>
            <p>📍 <strong>Format attendu pour ces moments clés</strong> :</p>
            <p>Indiquez-les au format <strong>00:00</strong> (minutes : secondes).</p>
            <p>💡 <strong>Quelques idées d’ajouts pertinents</strong> :</p>
            <ul>
              <li>🔗 Ajouter un lien externe (outil, article, fiche pratique)</li>
              <li>🧠 Proposer un quiz ou un exercice (externe à Teachizy)</li>
              <li>📚 Montrer un exemple complémentaire</li>
              <li>💡 Glisser une astuce rapide ou une bonne pratique</li>
              <li>📖 Définir un mot ou une notion-clé</li>
              <li>🎁 Mettre en avant une offre spéciale (bonus, réduction)</li>
              <li>🧑‍🏫 Offrir une séance de coaching</li>
              <li>🛍 Promouvoir un produit ou template associé</li>
              <li>🤔 Inviter à réfléchir ou à répondre à une question</li>
              <li>...</li>
            </ul>
          `.trim(),
          type_label: 'Choisir un type',
          type_button: 'Bouton',
          type_info: 'Bulle d\'information',
          type_card: 'Bulle cliquable',
          type_stop: 'Arrêt sur image',
          chapter_label: 'Libellé du chapitre',
          time_placeholder: '00:00',
          drag_handle_title: 'Cliquez et déplacez la souris pour ré-ordonner',
          add_cta: 'Ajouter une interaction',
          labels: {
            text: 'Texte',
            isClosed: 'Minimisé par défaut',
            startAt: 'Démarrer à',
            endAt: 'Arrêter à',
            title: 'Titre',
            ctaText: 'Texte du lien',
            ctaUrl: 'URL',
            position: 'Position',
          },
        },
      },
    },
  },
};
</script>

<template>
  <AppToggleSection titleTag="h3" titleSize="is-5">
    <template #title>
      {{ $t('ctas.title') }}
    </template>

    <template #default>
      <div class="notification">
        <p class="mb-3">
          <b-button
            icon-left="info"
            @click="hasTipsOpen = !hasTipsOpen"
            :aria-label="$t('video_tips_toggle.button_aria_label')"
          >
            {{ $t('video_tips_toggle.button_text') }}
          </b-button>
        </p>
        <div
          v-show="hasTipsOpen"
          class="box is-info content"
          v-html="$t('ctas.text')"
        />
        <hr class="has-background-grey">
        <Draggable
          class="block"
          v-bind="{ value }"
          @input="$emit('input', $event)"
          handle=".handle"
        >
          <div
            v-for="(cta) in ctas"
            :key="cta.id"
            class="box p-2 is-flex is-align-items-center mb-3"
          >
            <b-button
              type="is-ghost"
              size="is-small"
              class="handle mr-2"
              icon-left="grip-lines"
              :title="dragHandleTitle"
            />
            <span class="tag is-medium">
              {{ $t(`ctas.type_${cta.type.toLowerCase()}`) }}
            </span>
            <div class="buttons ml-auto">
              <b-button
                type="is-text"
                size="is-small"
                icon-left="edit"
                @click="editCta(cta)"
              />
              <b-button
                type="is-text"
                size="is-small"
                class="has-text-danger"
                icon-left="trash"
                @click="removeCta(cta)"
              />
            </div>
          </div>
        </Draggable>

        <div v-if="newCta" class="box">
          <div class="columns is-multiline pb-3">
            <div class="column is-12">
              <span class="tag is-medium">
                {{ $t(`ctas.type_${newCta.type.toLowerCase()}`) }}
              </span>
            </div>
            <!-- Button CTA -->
            <template v-if="newCta.type === 'BUTTON'">
              <div class="column pb-0 is-6">
                <b-field
                  :label="$t('ctas.labels.text')"
                  :label-for="`button-text-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.text, [
                    'required',
                  ])">
                  <b-input
                    :id="`button-text-${newCta.id}`"
                    v-model="newCta.text"
                    expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-6">
                <b-field
                  :label="$t('ctas.labels.ctaUrl')"
                  :label-for="`button-url-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.url, [
                    'required',
                    'url',
                  ])"
                >
                  <b-input
                    :id="`button-url-${newCta.id}`"
                    v-model="newCta.url"
                    type="url"
                    expanded
                  />
                </b-field>
              </div>
            </template>
            <!-- Info CTA -->
            <template v-if="newCta.type === 'INFO'">
              <div class="column pb-0 is-12">
                <b-field
                  :label="$t('ctas.labels.title')"
                  :label-for="`info-title-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.title, [
                    'required',
                  ])">
                  <b-input
                    :id="`info-title-${newCta.id}`"
                    v-model="newCta.title"
                    expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-12">
                <b-field
                  :label="$t('ctas.labels.text')"
                  :label-for="`info-text-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.text, [
                    'required',
                  ])">
                  <b-input
                    :id="`info-text-${newCta.id}`"
                    v-model="newCta.text"
                    type="textarea"
                    expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-12">
                <b-switch v-model="newCta.isClosed">
                  {{ $t('ctas.labels.isClosed') }}
                </b-switch>
              </div>
            </template>
            <!-- Card CTA -->
            <template v-if="newCta.type === 'CARD'">
              <div class="column pb-0 is-12">
                <b-field
                  :label="$t('ctas.labels.title')"
                  :label-for="`card-title-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.title, [
                    'required',
                  ])">
                  <b-input
                    :id="`card-title-${newCta.id}`"
                    v-model="newCta.title"
                      expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-12">
                <b-field
                  :label="$t('ctas.labels.text')"
                  :label-for="`card-text-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.text, [
                    'required',
                  ])">
                  <b-input
                    :id="`card-text-${newCta.id}`"
                    v-model="newCta.text"
                    type="textarea"
                    expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-6">
                <b-field
                  :label="$t('ctas.labels.ctaText')"
                  :label-for="`card-cta-text-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.ctaText, [
                    'required',
                  ])">
                  <b-input
                    :id="`card-cta-text-${newCta.id}`"
                    v-model="newCta.ctaText"
                    expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-6">
                <b-field
                  :label="$t('ctas.labels.ctaUrl')"
                  :label-for="`card-cta-url-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.ctaUrl, [
                    'required',
                    'url',
                  ])">
                  <b-input
                    :id="`card-cta-url-${newCta.id}`"
                    v-model="newCta.ctaUrl"
                    type="url"
                    expanded
                  />
                </b-field>
              </div>
            </template>
            <!-- Stop CTA -->
            <template v-if="newCta.type === 'STOP'">
              <div class="column pb-0 is-12">
                <b-field
                  :label="$t('ctas.labels.title')"
                  :label-for="`stop-title-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.title, [
                    'required',
                  ])"
                >
                  <b-input
                    :id="`stop-title-${newCta.id}`"
                    v-model="newCta.title"
                    expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-12">
                <b-field
                  :label="$t('ctas.labels.text')"
                  :label-for="`stop-text-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.text, [
                    'required',
                  ])"
                >
                  <b-input
                    type="textarea"
                    :id="`stop-text-${newCta.id}`"
                    v-model="newCta.text"
                    expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-6">
                <b-field
                  :label="$t('ctas.labels.ctaText')"
                  :label-for="`stop-cta-text-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.ctaText, [
                    'required',
                  ])">
                  <b-input
                    :id="`stop-cta-text-${newCta.id}`"
                    v-model="newCta.ctaText"
                    expanded
                  />
                </b-field>
              </div>
              <div class="column pb-0 is-6">
                <b-field
                  :label="$t('ctas.labels.ctaUrl')"
                  :label-for="`stop-cta-url-${newCta.id}`"
                  v-bind="$getErrorProps(v.newCta.ctaUrl, [
                    'required',
                    'url',
                  ])"
                >
                  <b-input
                    :id="`stop-cta-url-${newCta.id}`"
                    v-model="newCta.ctaUrl"
                    type="url"
                    expanded
                  />
                </b-field>
              </div>
            </template>
            <div v-if="newCta.type != 'STOP'" class="column pb-0 is-6">
              <b-field
                :label="$t('ctas.labels.position')"
                :label-for="`position-${newCta.id}`"
              >
                <BasePositionSelector
                  :id="`position-${newCta.id}`"
                  v-model="newCta.position"
                />
              </b-field>
            </div>
            <div class="column pb-0 is-6">
              <b-field
                :label="$t('ctas.labels.startAt')"
                :label-for="`start-at-${newCta.id}`"
                v-bind="$getErrorProps(v.newCta.startAt, [
                  'required',
                  'timecode',
                ])"
              >
                <b-input
                  :id="`start-at-${newCta.id}`"
                  v-model="newCta.startAt"
                  :placeholder="$t('ctas.time_placeholder')"
                  expanded
                  @input="newCta.type == 'STOP' && (newCta.endAt = null)"
                />
              </b-field>
              <b-field
                v-if="newCta.type != 'STOP'"
                :label="$t('ctas.labels.endAt')"
                :label-for="`end-at-${newCta.id}`"
                v-bind="$getErrorProps(v.newCta.endAt, [
                  { name: 'required' },
                  { name: 'timecode' },
                  { name: 'timecodeIsAfter', args: [newCta.startAt] },
                ])"
              >
                <b-input
                  :id="`end-at-${newCta.id}`"
                  v-model="newCta.endAt"
                  :placeholder="$t('ctas.time_placeholder')"
                  expanded
                />
              </b-field>
            </div>
            <div class="column pb-0 has-text-right">
              <hr>
              <b-button type="is-text" @click="closeNewCta">
                {{ $t('buttons.cancel') }}
              </b-button>
              <b-button native-type="submit" @click="saveCta">
                {{ $t('buttons.save') }}
              </b-button>
            </div>
          </div>
        </div>
        <div v-if="!newCta" class="field is-flex is-align-items-flex-end">
          <b-field class="mb-0 mr-3" :label="$t('ctas.type_label')" expanded>
            <b-select v-model="newCtaType" expanded>
              <option v-t="'ctas.type_button'" value="BUTTON" />
              <option v-t="'ctas.type_info'" value="INFO" />
              <option v-t="'ctas.type_card'" value="CARD" />
              <option v-t="'ctas.type_stop'" value="STOP" />
            </b-select>
          </b-field>
          <b-button @click="addCta">
            {{ addCtaButtonText }}
          </b-button>
        </div>
      </div>
    </template>
  </AppToggleSection>
</template>

<style scoped>
.handle:hover {
  text-decoration: none;
  cursor: grab;
}
</style>
